import React, { useCallback, useEffect, useRef, useState } from "react";
import { fmtDateTimeFirm } from "@/lib/dt";
import api, { toErrMsg, API_URL } from "@/lib/api";   // Batch 19 · Stage D: the one build setting
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Paperclip, Upload, Trash2, Download, FileText } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { fmtPlural } from "@/lib/format";
import { askConfirm } from "@/lib/appDialog";   // Batch 15 · N4 (F-118)

const ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,.csv,.xlsx,.xls";
const ALLOWED_EXTS = ["pdf", "png", "jpg", "jpeg", "webp", "csv", "xlsx", "xls"];
const MAX_BYTES = 25 * 1024 * 1024;

const fmtSize = (b) => {
  if (b === undefined || b === null) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * Reusable "Supporting documents" panel.
 *
 * Props:
 *  - engagementId  (string, required)
 *  - contextType   ("form" | "engagement" | "risk" | "sample" | "discrepancy")
 *  - contextId     (string, required — form_id, engagement_id, risk item id, etc.)
 *  - locked        (bool)  disable uploads/deletes (engagement signed off)
 *  - label         (string) header text (defaults to "Supporting documents")
 *  - compact       (bool)  tighter padding for row/dialog use
 */
export default function AttachmentPanel({
  engagementId, contextType, contextId,
  locked = false, label, compact = false, onChange = null,   // Batch 14 · M8 (F-107): the parent refreshes after an upload / delete
}) {
  const { lang, dict, t } = useI18n();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const displayLabel = label || t("attachments.label");

  const load = useCallback(async () => {
    if (!engagementId || !contextType || !contextId) return;
    try {
      const r = await api.get(`/attachments`, {
        params: { engagement_id: engagementId, context_type: contextType, context_id: contextId },
      });
      setItems(r.data || []);
    } catch {
      /* silent — page-level toast handles it */
    }
  }, [engagementId, contextType, contextId]);
  useEffect(() => { load(); }, [load]);

  const doUpload = async (files) => {
    if (locked) { toast.error(t("attachments.signedOffToast")); return; }
    const list = Array.from(files || []);
    if (list.length === 0) return;
    setBusy(true);
    let ok = 0;
    for (const f of list) {
      const ext = (f.name.split(".").pop() || "").toLowerCase();
      if (!ALLOWED_EXTS.includes(ext)) {
        toast.error(`${f.name} — unsupported type. Allowed: pdf, png, jpg, jpeg, webp, csv, xls, xlsx.`);
        continue;
      }
      if (f.size > MAX_BYTES) { toast.error(`${f.name} exceeds 25 MB limit`); continue; }
      const fd = new FormData();
      fd.append("file", f);
      fd.append("engagement_id", engagementId);
      fd.append("context_type", contextType);
      fd.append("context_id", contextId);
      try {
        await api.post("/attachments", fd, { headers: { "Content-Type": "multipart/form-data" }, _silent: true });
        ok += 1;
      } catch (e) {
        toast.error(`${f.name} — ${toErrMsg(e, "upload failed")}`);
      }
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
    if (ok > 0) toast.success(`${fmtPlural(ok, lang, dict.plurals.files)} attached`);
    await load();
    if (onChange) onChange();
  };

  const del = async (att) => {
    if (locked) return;
    if (!await askConfirm(`${t("attachments.deleteConfirm")} "${att.filename}"?`)) return;
    try {
      await api.delete(`/attachments/${att.id}`);
      toast.success(t("attachments.deleted"));
      await load();
      if (onChange) onChange();
    } catch (e) {
      toast.error(toErrMsg(e, t("common.failed")));
    }
  };

  const download = async (att) => {
    try {
      const token = localStorage.getItem("af_token");
      const resp = await fetch(
        `${API_URL}/attachments/${att.id}/download`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = att.filename; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("attachments.downloadFailed"));
    }
  };

  return (
    <div className={`rounded border bg-slate-50/60 ${compact ? "p-2" : "p-3"}`}
         data-testid={`attachments-${contextType}-${contextId}`}>
      <div className="mb-2 flex items-center gap-1.5 text-sm font-medium text-slate-700">
        <Paperclip className="h-3.5 w-3.5 text-emerald-600" />
        {displayLabel}
        {items.length > 0 && (
          <span className="ms-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] text-emerald-700"
                data-testid={`attach-count-${contextType}-${contextId}`}>
            {items.length}
          </span>
        )}
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); if (!locked && !busy) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault(); setDragOver(false);
          if (locked || busy) return;
          doUpload(e.dataTransfer.files);
        }}
        className={`rounded border-2 border-dashed p-3 text-center text-xs transition
                    ${dragOver ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white"}
                    ${locked ? "opacity-60" : ""}`}
        data-testid={`attach-dropzone-${contextType}-${contextId}`}
      >
        <input
          type="file"
          multiple
          ref={inputRef}
          accept={ACCEPT}
          disabled={locked || busy}
          onChange={(e) => doUpload(e.target.files)}
          className="hidden"
          data-testid={`attach-input-${contextType}-${contextId}`}
        />
        <div className="text-slate-600">
          <Upload className="mx-auto mb-1 h-4 w-4 text-slate-400" />
          {busy ? (
            t("digitalAudit.uploading")
          ) : locked ? (
            <span className="text-muted-foreground">{t("attachments.readOnly")}</span>
          ) : (
            <>
              {t("s2a3.dropOr")}{" "}
              <button
                type="button"
                className="font-medium text-emerald-600 underline-offset-2 hover:underline"
                onClick={() => inputRef.current?.click()}
                data-testid={`attach-btn-${contextType}-${contextId}`}
              >
                {t("s2a3.browse")}
              </button>
            </>
          )}
        </div>
        <div className="mt-1 text-[10px] text-muted-foreground">
          {t("attachments.formats")}
        </div>
      </div>

      {items.length > 0 && (
        <ul className="mt-2 space-y-1">
          {items.map((att) => (
            <li key={att.id}
                className="flex items-center gap-2 rounded border bg-white px-2 py-1.5 text-[12px]"
                data-testid={`attach-item-${att.id}`}>
              <FileText className="h-3.5 w-3.5 flex-none text-slate-500" />
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium" title={att.filename}>{att.filename}</div>
                <div className="text-[10px] text-muted-foreground">
                  {fmtSize(att.size)} · {att.uploaded_by_name || "—"}
                  {att.uploaded_at && ` · ${fmtDateTimeFirm(att.uploaded_at)}`}
                </div>
              </div>
              <Button size="icon" variant="ghost" className="h-6 w-6"
                      onClick={() => download(att)} data-testid={`attach-download-${att.id}`}
                      title={t("attachments.download")}>
                <Download className="h-3 w-3" />
              </Button>
              {!locked && (
                <Button size="icon" variant="ghost" className="h-6 w-6"
                        onClick={() => del(att)} data-testid={`attach-del-${att.id}`}
                        title={t("attachments.delete")}>
                  <Trash2 className="h-3 w-3 text-red-600" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
